/**
 * The ReviewsNav class for main page anchor scrolling.
 */
class ReviewsNav extends HTMLElement {
  constructor() {
    super();
    this.selectors = { tab: '[js-reviews-tab]' };
  }
  connectedCallback() {
    this.tabs = this.querySelectorAll(this.selectors.tab);
    this.sections = document.querySelectorAll('[data-section-id]');
    this.tabsKeys = Array.from(this.tabs).reduce((acc, tab) => {
      const id = tab.getAttribute('href').replace('#', '') || 'top';
      acc[id] = tab;
      return acc;
    }, {});
    this._setEventListeners();
    window.addEventListener('scroll', this._handleStickyScroll.bind(this));
  }
  _setEventListeners() {
    this.tabs.forEach((tab) =>
      tab.addEventListener('click', (evt) => {
        evt.preventDefault();
        const targetId = tab.getAttribute('href').replace('#', '') || 'top';
        if (targetId === 'top') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const targetSection = document.querySelector(`[data-section-id="${targetId}"]`);
          if (targetSection) targetSection.scrollIntoView({ behavior: 'smooth' });
        }
        this._setActiveTab(tab);
      })
    );
  }
  _findCurrentSection() {
    const scrollPosition = window.scrollY;
    if (scrollPosition === 0) return 'top';
    let closestSection = null;
    let minDistance = Infinity;
    this.sections.forEach((section) => {
      const distance = Math.abs(section.getBoundingClientRect().top);
      if (distance < minDistance) {
        minDistance = distance;
        closestSection = section;
      }
    });
    return closestSection ? closestSection.getAttribute('data-section-id') : null;
  }
  _setActiveTab(selectedTab) {
    this.tabs.forEach((tab) =>
      tab.classList.toggle('active', tab === selectedTab)
    );
  }
  _handleStickyScroll() {
    const currentSectionId = this._findCurrentSection();
    if (currentSectionId) {
      const selectedTab = this.tabsKeys[currentSectionId];
      if (selectedTab) this._setActiveTab(selectedTab);
    }
  }
}
if (!customElements.get('reviews-nav')) {
    customElements.define('reviews-nav', ReviewsNav);
}


/**
 * --- LOGIC FOR THE TABBED REVIEW SECTIONS ---
 * Definitive version with adaptive height logic and video count restoration.
 */
function initializeReviewSection(section) {
  const mobileButton = section.querySelector('.allReviews');
  const desktopButton = section.querySelector('.allReviewsDesktop');
  const tabs = section.querySelectorAll('.tab-container .tab');
  const okendoTab = section.querySelector('.tab[data-tab^="okendo-reviews-"]');

  if (!mobileButton || !desktopButton || !tabs.length || !okendoTab) {
    return;
  }
  
  // --- NEW: LOGIC TO COUNT VIDEOS AND UPDATE BADGE ---
  try {
    const sanitizedId = section.dataset.sectionId;
    const videoBadge = section.querySelector(`#video-review-badge-${sanitizedId}`);
    const videoSlides = section.querySelectorAll(`#video-reviews-${sanitizedId} .reviews-section__slide`);
    
    if (videoBadge && videoSlides) {
      videoBadge.textContent = videoSlides.length;
    }
  } catch (e) {
    console.error("Error updating video count:", e);
  }
  // --- END NEW LOGIC ---

  const correctorLoop = () => {
    if (!okendoTab.classList.contains('active')) return;

    const hooperTrack = section.querySelector('.hooper-track');
    if (!hooperTrack) return;

    const isMobile = window.innerWidth < 1025;
    let maxHeight = 0;

    // Height Correction with Adaptive Logic
    if (isMobile) {
      const currentSlide = section.querySelector('.hooper-slide.is-current');
      if (currentSlide) {
        const reviewCard = currentSlide.querySelector('.oke-reviewCard');
        if (reviewCard) {
            maxHeight = reviewCard.offsetHeight;
            const button = currentSlide.querySelector('.allReviews');
            if (button && button.style.display !== 'none') {
                const buttonStyle = window.getComputedStyle(button);
                const buttonMarginTop = parseInt(buttonStyle.marginTop, 10) || 0;
                const buttonMarginBottom = parseInt(buttonStyle.marginBottom, 10) || 0;
                maxHeight += button.offsetHeight + buttonMarginTop + buttonMarginBottom;
            }
        }
      }
    } else { // Desktop
      const activeSlides = section.querySelectorAll('.hooper-slide.is-active');
      if (activeSlides.length > 0) {
        activeSlides.forEach(slide => {
          const reviewCard = slide.querySelector('.oke-reviewCard');
          if (reviewCard && reviewCard.offsetHeight > maxHeight) {
            maxHeight = reviewCard.offsetHeight;
          }
        });
      }
    }
    
    if (maxHeight > 0) {
      maxHeight += 5; // Safety buffer
    }

    if (maxHeight > 0 && hooperTrack.offsetHeight !== maxHeight) {
      hooperTrack.style.height = `${maxHeight}px`;
    }

    // Button Position Correction
    if (isMobile) {
      desktopButton.style.display = 'none';
      const currentSlide = section.querySelector('.hooper-slide.is-current');
      if (currentSlide) {
        mobileButton.style.display = 'block';
        // Re-appending an already-placed node detaches it and cancels in-flight clicks.
        if (mobileButton.parentElement !== currentSlide) {
          currentSlide.appendChild(mobileButton);
        }
      } else {
        mobileButton.style.display = 'none';
      }
    } else { // Desktop
      mobileButton.style.display = 'none';
      const header = section.querySelector('.oke-reviewCarousel-header');
      if (header) {
        desktopButton.style.display = 'block';
        if (desktopButton.parentElement !== header) {
          header.appendChild(desktopButton);
        }
      } else {
        desktopButton.style.display = 'none';
      }
    }
  };

  // Simplified Tab Click Listener
  tabs.forEach(clickedTab => {
    clickedTab.addEventListener('click', (event) => {
      event.preventDefault();
      if (clickedTab.classList.contains('active')) return;
      tabs.forEach(tab => tab.classList.remove('active'));
      section.querySelectorAll('.tab-content').forEach(panel => panel.classList.remove('active'));
      clickedTab.classList.add('active');
      const contentId = clickedTab.dataset.tab;
      const activeContent = section.querySelector(`#${contentId}`);
      if (activeContent) activeContent.classList.add('active');
    });
  });

  // Start the single, persistent corrector loop.
  setInterval(correctorLoop, 150);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.reviews-section').forEach(initializeReviewSection);
});